import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ThreadChat from '../components/factory/ThreadChat';
import StarRating from '../components/market/StarRating';
import { marketImageUrl } from '../lib/marketCatalog';
import { supplierStars } from '../lib/marketRating';
import { shortSupplierName } from '../lib/marketSupplierName';
import { EXPECTED_REPLY, fetchInquiry, fetchInquiryMessages, markInquiryRead, sendInquiry, supplierCity } from '../lib/marketInquiries';

// «اسأل المورد» — the trader's conversation about one store product (/market/inquiries/:id).
// Phase 1: staff relay the question and post the supplier's answer; the trader
// sees the supplier's words, translated, and nothing about how they travelled.
// Rating only when the product carries a real one (never invented).
const T = {
  ar: { supplier: 'المورد', empty: 'اكتب سؤالك عن المنتج — المقاس، الوزن، الخامة، التغليف…', loading: 'جارٍ التحميل…', notFound: 'المحادثة غير موجودة.', signin: 'سجّل الدخول لسؤال المورد.', login: 'تسجيل الدخول', product: 'المنتج', team: 'فريق معبر', priceReq: 'طلب تثبيت سعر الشحن' },
  en: { supplier: 'Supplier', empty: 'Ask about the product — size, weight, material, packaging…', loading: 'Loading…', notFound: 'Conversation not found.', signin: 'Sign in to ask the supplier.', login: 'Sign in', product: 'Product', team: 'MAABAR team', priceReq: 'Shipping price request' },
  zh: { supplier: '供应商', empty: '询问产品：尺寸、重量、材质、包装…', loading: '加载中…', notFound: '未找到对话。', signin: '登录后咨询供应商。', login: '登录', product: '产品', team: 'MAABAR 团队', priceReq: '运费确认请求' },
};
const centered = { display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 'var(--app-dvh)', gap: 14, flexDirection: 'column', padding: 24, textAlign: 'center' };

export function PinnedProduct({ pr, lang, onOpen }) {
  const title = (lang === 'ar' ? pr.title_ar : lang === 'zh' ? pr.title_zh : pr.title_en) || pr.title_en || pr.title_ar || '';
  return (
    <button type="button" data-pinned-product={pr.product_id} onClick={onOpen}
      style={{ display: 'flex', gap: 10, alignItems: 'center', width: '100%', textAlign: 'start', background: '#fff', border: '1px solid rgba(0,0,0,0.1)',
        borderRadius: 12, padding: 8, cursor: 'pointer', marginBottom: 6, fontFamily: 'inherit' }}>
      {pr.image_ref && <img src={marketImageUrl(pr.image_ref, true)} alt="" style={{ width: 52, height: 52, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />}
      <span style={{ fontSize: 13, lineHeight: 1.45, color: 'rgba(0,0,0,0.8)', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{title}</span>
    </button>
  );
}

export default function MarketInquiry({ user, lang = 'ar' }) {
  const { id } = useParams();
  const nav = useNavigate();
  const isAr = lang === 'ar';
  const t = T[lang] || T.ar;
  const [inq, setInq] = useState(null);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    if (!user) return undefined;
    let alive = true;
    fetchInquiry(id).then(({ inquiry }) => { if (!alive) return; setInq(inquiry); setStatus(inquiry ? 'ok' : 'notfound'); });
    return () => { alive = false; };
  }, [id, user]);

  const load = useCallback(async () => { const m = await fetchInquiryMessages(id); markInquiryRead(id); return m; }, [id]);
  const send = useCallback(async (body) => { const r = await sendInquiry(id, body); if (!r.ok) throw new Error(r.error); }, [id]);

  if (!user) return (
    <div className="full-page" dir={isAr ? 'rtl' : 'ltr'} style={centered}>
      <p style={{ color: 'var(--text-secondary)' }}>{t.signin}</p>
      <button onClick={() => nav('/login?next=' + encodeURIComponent(`/market/inquiries/${id}`))} style={{ background: '#1a1814', color: '#fff', border: 'none', padding: '10px 24px', borderRadius: 8, cursor: 'pointer' }}>{t.login}</button>
    </div>
  );
  if (status !== 'ok') return <div className="full-page" dir={isAr ? 'rtl' : 'ltr'} style={centered}><p style={{ color: 'var(--text-secondary)' }}>{status === 'loading' ? t.loading : t.notFound}</p></div>;

  const pr = inq.product_ref || {};
  const stars = supplierStars(pr.seller_rating);
  const city = supplierCity(pr.origin_region, lang);
  const reply = EXPECTED_REPLY[lang] || EXPECTED_REPLY.ar;
  // «اطلب تثبيت سعر الشحن» (unified 2026-11-01): the team answers here; the card opens the order to pay
  const priceReq = inq.kind === 'price_request' && inq.order_id;
  return (
    <ThreadChat
      lang={lang}
      selfRole="trader"
      header={priceReq ? { name: t.team, meta: [t.priceReq, pr.order_no].filter(Boolean).join(' · ') } : { name: shortSupplierName(pr.seller_name) || t.supplier, meta: [city, reply].filter(Boolean).join(' · ') }}
      headerExtra={stars ? <span data-supplier-rating={stars.text}><StarRating rating={pr.seller_rating} compact size={13} /></span> : null}
      showChinaTime
      emptyText={t.empty}
      onBack={() => nav(-1)}
      loadMessages={load}
      sendMessage={send}
      translationTable="market_inquiry_translations"
      pinned={<PinnedProduct pr={pr} lang={lang} onOpen={() => (priceReq ? nav(`/market/orders/${inq.order_id}`) : pr.product_id && nav(`/market/${pr.product_id}`))} />}
    />
  );
}
